import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { SalesActivityTimeRange } from '../../utils/leadAnalytics';
import { SalesActivityPeriodDetail as PeriodDetail } from '../../utils/salesActivityTimeline';

interface Props {
  detail: PeriodDetail;
  timeRange: SalesActivityTimeRange;
  formatCurrency: (n: number) => string;
  onClose: () => void;
}

function dealLabel(deal: PeriodDetail['wonDeals'][number]): string {
  return deal.contactName || deal.title || deal.company || 'Deal';
}

const SalesActivityPeriodDetail: React.FC<Props> = ({
  detail,
  timeRange,
  formatCurrency,
  onClose,
}) => {
  const navigate = useNavigate();
  const heading = detail.date.toLocaleDateString('en-US', {
    weekday: 'short',
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  });

  return (
    <div className="fin-proj-detail-card">
      <div className="fin-proj-detail-head">
        <h3>{heading} — activity</h3>
        <button
          type="button"
          className="fin-secondary-btn"
          onClick={onClose}
          aria-label="Close day detail"
        >
          Close
        </button>
      </div>
      <div className="fin-proj-detail-cols">
        <div className="fin-proj-detail-col">
          <div className="fin-proj-detail-col-head fin-proj-detail-col-head--in">
            Leads in · {detail.leadCount}
          </div>
          {detail.leadsBySource.length === 0 && (
            <div className="fin-proj-detail-empty">No leads this day.</div>
          )}
          {detail.leadsBySource.map(row => (
            <button
              key={row.source}
              type="button"
              className="fin-proj-detail-row sa-period-row"
              onClick={() => {
                navigate('/', { state: { sourceFilter: row.source, dateRange: timeRange } });
              }}
            >
              <div className="fin-proj-detail-row-main">
                <span className="fin-proj-detail-project">
                  <span className="sa-source-dot" style={{ background: row.color }} />
                  {row.source}
                </span>
                <span className="fin-proj-detail-amt fin-proj-detail-amt--in">{row.count}</span>
              </div>
              <div className="fin-proj-detail-row-sub">
                <span>Open in Leads</span>
              </div>
            </button>
          ))}
        </div>
        <div className="fin-proj-detail-col">
          <div className="fin-proj-detail-col-head fin-proj-detail-col-head--out">
            Lost · {detail.lostDeals.length}
          </div>
          {detail.lostDeals.length === 0 && (
            <div className="fin-proj-detail-empty">No lost deals this day.</div>
          )}
          {detail.lostDeals.map(deal => {
            const inner = (
              <>
                <div className="fin-proj-detail-row-main">
                  <span className="fin-proj-detail-project">{dealLabel(deal)}</span>
                  <span className="fin-proj-detail-amt fin-proj-detail-amt--out">
                    {formatCurrency(deal.value || 0)}
                  </span>
                </div>
                <div className="fin-proj-detail-row-sub">
                  <span>{deal.title}</span>
                  {deal.leadId ? <span>Open lead</span> : <span>No linked lead</span>}
                </div>
              </>
            );
            return deal.leadId ? (
              <Link
                key={deal.id || deal.leadId}
                to={`/client/${deal.leadId}`}
                className="fin-proj-detail-row sa-period-row"
              >
                {inner}
              </Link>
            ) : (
              <div key={deal.id || deal.title} className="fin-proj-detail-row">
                {inner}
              </div>
            );
          })}
        </div>
      </div>
      <div className="fin-proj-detail-footer">
        <div>
          Won <strong>{detail.wonDeals.length}</strong>
          {' · '}
          revenue <strong>{formatCurrency(detail.wonRevenue)}</strong>
          {' · '}
          change orders <strong>{formatCurrency(detail.changeOrderRevenue)}</strong>
          {' · '}
          design fees <strong>{formatCurrency(detail.designFeeTotal)}</strong>
        </div>
        {detail.changeOrders.length > 0 && (
          <div className="sa-period-won-list sa-period-co-list">
            <div className="fin-proj-detail-col-head fin-proj-detail-col-head--in">
              Change orders approved · {detail.changeOrders.length}
            </div>
            {detail.changeOrders.map(co => {
              const href = co.slug
                ? `/change-order/${co.slug}`
                : co.leadId
                  ? `/client/${co.leadId}`
                  : null;
              const subtitle = [co.clientName, co.projectName].filter(Boolean).join(' · ');
              const inner = (
                <>
                  <div className="fin-proj-detail-row-main">
                    <span className="fin-proj-detail-project">
                      {co.reference} · {co.title}
                    </span>
                    <span className="fin-proj-detail-amt fin-proj-detail-amt--in">
                      {formatCurrency(co.deltaAmount)}
                    </span>
                  </div>
                  <div className="fin-proj-detail-row-sub">
                    <span>{subtitle || 'Change order'}</span>
                    <span>{href ? (co.slug ? 'Open change order' : 'Open lead') : 'No link'}</span>
                  </div>
                </>
              );
              return href ? (
                <Link key={co.id} to={href} className="sa-period-won-row sa-period-co-row">
                  {inner}
                </Link>
              ) : (
                <div key={co.id} className="sa-period-won-row sa-period-co-row">
                  {inner}
                </div>
              );
            })}
          </div>
        )}
        {detail.wonDeals.length > 0 && (
          <div className="sa-period-won-list">
            {detail.wonDeals.map(deal => {
              const inner = (
                <>
                  <span className="fin-proj-detail-project">{dealLabel(deal)}</span>
                  <span className="fin-proj-detail-amt fin-proj-detail-amt--in">
                    {formatCurrency(deal.value || 0)}
                  </span>
                </>
              );
              return deal.leadId ? (
                <Link
                  key={deal.id || deal.leadId}
                  to={`/client/${deal.leadId}`}
                  className="sa-period-won-row"
                >
                  {inner}
                </Link>
              ) : (
                <div key={deal.id || deal.title} className="sa-period-won-row">
                  {inner}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default SalesActivityPeriodDetail;
